// OnboardingWizard V2 — first-time setup wizard (D-08).
// Caller-controlled render: callers use {open && <OnboardingWizard />}; no isOpen prop.
// Owner: 6 steps — Terms, Profile, Brokerage, Connect Google, SMS Notifications, Welcome.
// Member: 5 steps — same minus Brokerage.
// There is no Role step and the payload carries no role: App keeps the stored
// role and TC mode, and a brand-new account defaults to 'realtor' (C-CR-01).
//
// SA-01: wizard is reopenable on demand from the Help drawer without destructively
// clearing persisted user data (no wipe on open; fields initialised from props).

import React from 'react';
import ReactDOM from 'react-dom';
import useMediaQuery from '../../hooks/useMediaQuery';
import { isWorkspaceOwner } from '../features/settings/integrationsView';
import useGoogleOAuthStatus from '../../hooks/useGoogleOAuthStatus';
import { updateWorkspaceSettings } from '../../services/workspaceService';
import { setMemberMessagingProfile } from '../../services/messagingService';
import { toE164, formatNational } from '../../utils/phoneNumbers';
import { formatPhoneNumber } from '../../utils/helpers';
import { useTour } from '../features/tours/TourContext';
import { useToast } from '../../contexts/ToastContext';
import useDialogShell from '../ui/primitives/useDialogShell';
import { SMS_CONSENT_TEXT } from '../../config/smsConsent';
import { listTimeZones, formatClockInZone } from '../../utils/timezoneOptions';
import { storedWorkspaceTimezone } from '../features/settings/workspaceTimezone';
import Field from '../ui/primitives/Field';
import Button from '../ui/primitives/Button';
import Select from '../ui/primitives/Select';
import { oauthErrorText } from '../../utils/googleConnection';
import Input from '../ui/primitives/Input';
import BrokeragePicker from '../features/settings/brokerage/BrokeragePicker';
import { planLabelFor } from '../features/settings/brokerage/PastBrokeragesList';
import { buildInitialBrokerage, buildSwitchPatch, newStintId, normalizeBrokerage } from '../../commissions/brokerage';
import { getPresetPlan } from '../../commissions/presets';
import { sanitizePlan } from '../../commissions/modules/plan/schema';
import { stripUndefinedDeep } from '../../utils/stripUndefinedDeep';

// Today's date in the owner's local time as YYYY-MM-DD (the join date of the first stint).
const todayLocalISO = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// ── Step labels ──────────────────────────────────────────────────────────────
const OWNER_STEP_LABELS = [
    'Terms',
    'Profile',
    'Brokerage',
    'Connect Google',
    'SMS Notifications',
    'Welcome',
];
const MEMBER_STEP_LABELS = [
    'Terms',
    'Profile',
    'Connect Google',
    'SMS Notifications',
    'Welcome',
];

// ── Progress bar ─────────────────────────────────────────────────────────────
function ProgressBar({ stepIndex, total }) {
    const pct = Math.round(((stepIndex + 1) / total) * 100);
    return (
        <div
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            style={{ height: 4, backgroundColor: 'var(--border-color)' }}
        >
            <div
                style={{
                    height: '100%',
                    width: `${pct}%`,
                    backgroundColor: 'var(--color-accent)',
                    transition: 'width 200ms ease-out',
                }}
            />
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
function OnboardingWizard({
    initialFirstName = '',
    initialLastName = '',
    user = null,
    workspace = null,
    selectedWorkspaceId = '',
    onComplete,
}) {
    const isMobile = useMediaQuery('(max-width: 767px)');
    const isOwner = isWorkspaceOwner(user, workspace);
    // Help → restart onboarding reruns the Brokerage step. A brokerage that already
    // exists is shown, never rebuilt: rebuilding drops the history closed deals are
    // priced under. An eXp workspace from before brokerages (no map, but an
    // anniversary) that picks another brokerage is switching, like Change brokerage.
    const existingBrokerage = workspace?.brokerage?.current?.id ? normalizeBrokerage(workspace) : null;
    const isLegacyExpWorkspace = !existingBrokerage && Boolean(workspace?.expModuleSettings?.anniversaryDate);

    // Dialog-shell behaviors: scroll-lock, focus-on-open + focus-return, and Tab focus
    // trap. closeOnEscape:false — the wizard is non-dismissible (user must complete or
    // explicitly skip via the Welcome step buttons). No Escape handler is registered.
    const { containerRef } = useDialogShell({
        onClose: () => {}, // stable no-op: wizard is non-dismissible via Escape
        closeOnEscape: false,
        trapFocus: true,
        lockScroll: true,
    });
    const STEP_LABELS = isOwner ? OWNER_STEP_LABELS : MEMBER_STEP_LABELS;

    // Step state
    const [stepIndex, setStepIndex] = React.useState(0);

    // Desktop modal height: measure each step's natural content height and keep the
    // TALLEST seen, then use it as the body's minHeight. This makes the modal match
    // the tallest step in the flow — short steps no longer shrink it (footer/Continue
    // stay put) and it is never taller than the tallest actual step. Mobile is full-dvh.
    const bodyContentRef = React.useRef(null);
    const [maxBodyHeight, setMaxBodyHeight] = React.useState(0);
    React.useLayoutEffect(() => {
        if (isMobile) return;
        const el = bodyContentRef.current;
        if (!el) return;
        const h = el.offsetHeight;
        if (h > 0) setMaxBodyHeight((prev) => (h > prev ? h : prev));
    }, [stepIndex, isMobile]);

    // Step 1 — Terms
    const [termsAccepted, setTermsAccepted] = React.useState(false);

    // Step 2 — Profile
    const [firstName, setFirstName] = React.useState(initialFirstName);
    const [lastName, setLastName] = React.useState(initialLastName);
    const [nameError, setNameError] = React.useState('');

    // Step 3 (owner only) — Brokerage. Nothing is preselected: an empty brokerageId
    // means the owner has not chosen yet.
    const [brokerageChoice, setBrokerageChoice] = React.useState({ brokerageId: '', planId: '', brokerageName: '' });
    const [capYearStart, setCapYearStart] = React.useState('');
    const [customSplit, setCustomSplit] = React.useState('');
    const [customCap, setCustomCap] = React.useState('');
    const [expAnniversary, setExpAnniversary] = React.useState('');
    const [expAnniversaryError, setExpAnniversaryError] = React.useState('');
    const [expSaving, setExpSaving] = React.useState(false);
    // D-12: never preselect a zone on the user's behalf. The browser's own report is
    // shown as the selection (and saved only when they press Continue); when the
    // browser cannot report one, the picker starts empty and Continue stays disabled.
    // A stored zone counts only when it is valid (D-12, the same check the
    // Automations card makes), so an invalid one is never prefilled and saved back.
    const initialTimezone = () => {
        const stored = storedWorkspaceTimezone(workspace);
        if (stored) return stored;
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        } catch {
            return '';
        }
    };
    const [timezone, setTimezone] = React.useState(initialTimezone);
    const timezoneOptions = React.useMemo(() => {
        const list = listTimeZones();
        return !timezone || list.includes(timezone) ? list : [timezone, ...list];
    }, [timezone]);

    // Connect Google (handled by hook)
    const [googleError, setGoogleError] = React.useState('');
    const oauth = useGoogleOAuthStatus(user, (err) => setGoogleError(oauthErrorText(err, 'Google connection failed. Please try again.')));

    // SMS Notifications
    const [smsPhone, setSmsPhone] = React.useState('');
    const [smsConsent, setSmsConsent] = React.useState(false);
    const [smsError, setSmsError] = React.useState('');

    // Welcome (tour offer)
    const { launchTour, markSeen } = useTour();
    const { showToast } = useToast();

    // Reset when the wizard is remounted (SA-01: re-opened on demand)
    React.useEffect(() => {
        setStepIndex(0);
        setTermsAccepted(false);
        setFirstName(initialFirstName);
        setLastName(initialLastName);
        setExpAnniversary('');
        setExpAnniversaryError('');
        setBrokerageChoice({ brokerageId: '', planId: '', brokerageName: '' });
        setCapYearStart('');
        setCustomSplit('');
        setCustomCap('');
        setTimezone(initialTimezone());
        setGoogleError('');
        setSmsPhone('');
        setSmsConsent(false);
        setSmsError('');
        setNameError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Step index of each logical step ──────────────────────────────────────
    // STEP_LABELS indices:
    //   Owner:  0=Terms 1=Profile 2=Brokerage 3=Google 4=SMS 5=Welcome
    //   Member: 0=Terms 1=Profile       2=Google 3=SMS 4=Welcome
    const BROKERAGE_STEP = isOwner ? 2 : -1;
    const GOOGLE_STEP = isOwner ? 3 : 2;
    const SMS_STEP    = isOwner ? 4 : 3;
    const WELCOME_STEP = isOwner ? 5 : 4;

    const currentLabel = STEP_LABELS[stepIndex];

    // ── Navigation helpers ────────────────────────────────────────────────────
    const handleBack = () => {
        if (stepIndex > 0) setStepIndex((prev) => prev - 1);
    };

    const handleNext = async () => {
        // Step 0 — Terms
        if (stepIndex === 0) {
            if (!termsAccepted) return;
            setStepIndex(1);
            return;
        }

        // Step 1 — Profile
        if (stepIndex === 1) {
            const trimFirst = (firstName || '').trim();
            const trimLast = (lastName || '').trim();
            if (!trimFirst || !trimLast) {
                setNameError('Enter both your first and last name to continue.');
                return;
            }
            setNameError('');
            setStepIndex(2);
            return;
        }

        // Step 2 — Brokerage (owner only, required)
        if (stepIndex === BROKERAGE_STEP) {
            const { brokerageId, planId, brokerageName } = brokerageChoice;
            if (existingBrokerage) {
                if (!selectedWorkspaceId) {
                    setExpAnniversaryError('No workspace is selected, so this cannot be saved right now.');
                    return;
                }
                setExpSaving(true);
                try {
                    await updateWorkspaceSettings(selectedWorkspaceId, {
                        'milestoneDigestAutomation.timezone': timezone,
                        'teamMessageDigestAutomation.timezone': timezone,
                    });
                } catch {
                    setExpAnniversaryError("Couldn't save these settings. Please try again.");
                    setExpSaving(false);
                    return;
                }
                setExpSaving(false);
                setStepIndex((prev) => prev + 1);
                return;
            }
            if (!brokerageId) {
                setExpAnniversaryError('Choose your brokerage to continue.');
                return;
            }
            const preset = brokerageId === 'exp' || brokerageId === 'custom' ? null : getPresetPlan(brokerageId, planId);
            const needsCapYear = !!(preset && preset.cap && preset.capYear.reset === 'anniversary');
            if (brokerageId === 'exp' && !expAnniversary) {
                setExpAnniversaryError('Enter your eXp anniversary date to continue.');
                return;
            }
            if (needsCapYear && !capYearStart) {
                setExpAnniversaryError('Enter your anniversary date to continue.');
                return;
            }
            const splitNumber = customSplit.trim() === '' ? NaN : Number(customSplit);
            if (brokerageId === 'custom' && !(splitNumber >= 0 && splitNumber <= 100)) {
                setExpAnniversaryError('Enter your brokerage split to continue.');
                return;
            }
            // Defensive guard (bug: 2026-09-24-onboarding-wizard-before-plan-choice.md):
            // this step writes to a workspace document, so refuse rather than silently
            // writing nowhere (or erroring past a caller that never checked) when no
            // workspace is selected.
            if (!selectedWorkspaceId) {
                setExpAnniversaryError('No workspace is selected, so this cannot be saved right now.');
                return;
            }
            setExpAnniversaryError('');
            setExpSaving(true);
            try {
                // Convert YYYY-MM-DD (from <input type="date">) to MM-DD format
                const toMonthDay = (iso) => iso.split('-').slice(1).join('-');
                // Ruling R20: the first stint has no join date, so it owns the
                // deals an agent back-enters from before signup. An anniversary
                // plan therefore carries its anchor explicitly: the entered
                // cap-year date, or today's MM-DD for a plan with no date field.
                const withAnchor = (plan, monthDay) => (plan.capYear.reset === 'anniversary'
                    ? { ...plan, capYear: { ...plan.capYear, anchorDate: monthDay } }
                    : plan);
                const todayMonthDay = toMonthDay(todayLocalISO());
                // A legacy eXp workspace picking another brokerage freezes eXp into
                // history and starts the new plan today, exactly as Change brokerage does.
                const brokerageFor = (choice) => (isLegacyExpWorkspace
                    ? buildSwitchPatch(normalizeBrokerage(workspace), workspace.expModuleSettings, { ...choice, joinedAt: todayLocalISO() }, newStintId()).brokerage
                    : buildInitialBrokerage(choice).brokerage);
                const update = {
                    // Dot paths, exactly as TimezoneField writes them, so no other
                    // automation field is clobbered.
                    'milestoneDigestAutomation.timezone': timezone,
                    'teamMessageDigestAutomation.timezone': timezone,
                };
                if (brokerageId === 'exp') {
                    update['expModuleSettings.anniversaryDate'] = toMonthDay(expAnniversary);
                    update.brokerage = buildInitialBrokerage({ brokerageId: 'exp', planId: 'standard' }).brokerage;
                } else if (brokerageId === 'custom') {
                    const capAmount = customCap.trim() === '' ? null : Number(customCap);
                    const plan = withAnchor(sanitizePlan({
                        split: { brokerPct: splitNumber },
                        cap: capAmount ? { amount: capAmount, postCapBrokerPct: 0 } : null,
                    }), todayMonthDay);
                    update.brokerage = brokerageFor({ brokerageId, planId: 'custom', plan, brokerageName });
                } else {
                    const plan = preset ? withAnchor(preset, needsCapYear ? toMonthDay(capYearStart) : todayMonthDay) : undefined;
                    update.brokerage = brokerageFor({ brokerageId, planId, plan });
                }
                await updateWorkspaceSettings(selectedWorkspaceId, stripUndefinedDeep(update));
            } catch {
                setExpAnniversaryError("Couldn't save these settings. Please try again.");
                setExpSaving(false);
                return;
            }
            setExpSaving(false);
            setStepIndex((prev) => prev + 1);
            return;
        }

        // Step — Connect Google (optional, skip advances)
        if (stepIndex === GOOGLE_STEP) {
            setStepIndex((prev) => prev + 1);
            return;
        }

        // Step — SMS Notifications (optional)
        if (stepIndex === SMS_STEP) {
            const normalizedPhone = toE164(smsPhone);
            const hasValidPhone = normalizedPhone !== null;
            if (hasValidPhone && smsConsent) {
                // Fire-and-forget: advance immediately; save runs in the background.
                // SMS prefs are optional and editable later in Settings, so a failure
                // here is non-fatal. The callable still fires — data is not lost.
                setMemberMessagingProfile({
                    workspaceId: selectedWorkspaceId,
                    phone: normalizedPhone,
                    consentGranted: true,
                }).catch((err) => {
                    console.error('setMemberMessagingProfile background save failed', err);
                });
            }
            setStepIndex((prev) => prev + 1);
            return;
        }
    };

    // No `role`: the wizard never asks for one, so App keeps the stored role and
    // TC mode (a replaying TC stays a TC; a brand-new account becomes a realtor).
    const buildPayload = () => ({
        firstName: (firstName || '').trim(),
        lastName: (lastName || '').trim(),
        termsAccepted: true,
        acceptedTermsVersion: 'v1',
        acceptedAt: Date.now(),
    });

    // D-24: the wizard never derives a tour's completion status itself. It
    // either launches the tour and lets TourContext's onDestroyStarted own
    // the lifecycle status, or records a declined prompt as 'dismissed' —
    // not 'skipped', because no tour was ever offered.
    const handleStartTour = async () => {
        // Complete (unmount) the wizard first so driver.js mounts over a clean page.
        // Pass landOnDashboard:true so the App-side handler navigates to "/" instead
        // of the default "/business-partners/realtors" — the welcome tour route is
        // Dashboard, and both must agree to avoid the overlay-mismatch race (Finding B).
        if (typeof onComplete === 'function') onComplete(buildPayload(), { landOnDashboard: true });
        const result = await launchTour('welcome');
        if (!result.ok) {
            showToast(result.hint, 'error');
        }
    };

    const handleSkipTour = async () => {
        if (typeof onComplete === 'function') onComplete(buildPayload());
        await markSeen('welcome', 'dismissed');
    };

    // ── Disabled states ───────────────────────────────────────────────────────
    const isContinueDisabled =
        (stepIndex === 0 && !termsAccepted) ||
        (stepIndex === BROKERAGE_STEP && !timezone) ||
        (stepIndex === BROKERAGE_STEP && expSaving) ||
        (stepIndex === SMS_STEP && smsPhone.trim() !== '' && toE164(smsPhone) === null);

    const isLastStep = stepIndex === WELCOME_STEP;
    const showBackButton = stepIndex > 0 && !isLastStep;

    // ── Step renderers ────────────────────────────────────────────────────────
    const renderTermsStep = () => (
        <div className="space-y-6" data-tour="onboarding-terms">
            <div>
                <h2
                    id="onboarding-dialog-title"
                    className="text-2xl font-semibold text-[color:var(--text-primary)]"
                    style={{ letterSpacing: '-0.02em' }}
                >
                    Welcome to KeyFlow
                </h2>
                <p className="mt-2 text-sm text-[color:var(--text-muted)]">
                    Before we get you set up, please review and accept our Terms of Service and Privacy Policy.
                </p>
            </div>
            <div
                className="rounded-lg border border-[color:var(--border-color)] p-4"
                style={{ backgroundColor: 'var(--surface-muted)' }}
            >
                <p className="text-sm text-[color:var(--text-muted)] leading-relaxed">
                    KeyFlow helps you keep transactions organized, collaborate with clients and partners, and stay on top of critical dates and deadlines.
                </p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
                <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-[color:var(--border-color)] text-[color:var(--color-accent)] focus:ring-[color:var(--color-accent)]/25"
                />
                <span className="text-sm text-[color:var(--text-muted)]">
                    I have read and agree to the KeyFlow{' '}
                    <a
                        href="https://keyflow.homes/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[color:var(--action-link-color)] underline-offset-2 hover:underline"
                    >
                        Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                        href="https://keyflow.homes/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[color:var(--action-link-color)] underline-offset-2 hover:underline"
                    >
                        Privacy Policy
                    </a>
                    .
                </span>
            </label>
        </div>
    );

    const renderProfileStep = () => (
        <div className="space-y-6" data-tour="onboarding-profile">
            <div>
                <h2
                    className="text-2xl font-semibold text-[color:var(--text-primary)]"
                    style={{ letterSpacing: '-0.02em' }}
                >
                    Tell us about yourself
                </h2>
                <p className="mt-2 text-sm text-[color:var(--text-muted)]">
                    Your name will appear in emails, client communications, and reports.
                </p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                    <label
                        className="block text-sm font-medium text-[color:var(--text-muted)]"
                        htmlFor="onboarding-firstName"
                    >
                        First Name
                    </label>
                    <input
                        id="onboarding-firstName"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                        placeholder="Alex"
                        className="mt-1 w-full rounded-md border border-[color:var(--border-color)] px-3 py-2 focus:border-[color:var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]/25"
                    />
                </div>
                <div>
                    <label
                        className="block text-sm font-medium text-[color:var(--text-muted)]"
                        htmlFor="onboarding-lastName"
                    >
                        Last Name
                    </label>
                    <input
                        id="onboarding-lastName"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        autoComplete="family-name"
                        placeholder="Taylor"
                        className="mt-1 w-full rounded-md border border-[color:var(--border-color)] px-3 py-2 focus:border-[color:var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]/25"
                    />
                </div>
            </div>
            {nameError && (
                <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{nameError}</p>
            )}
        </div>
    );

    const renderBrokerageStep = () => {
        const { brokerageId, planId } = brokerageChoice;
        const preset = brokerageId === 'exp' || brokerageId === 'custom' ? null : getPresetPlan(brokerageId, planId);
        const needsCapYear = !!(preset && preset.cap && preset.capYear.reset === 'anniversary');
        const clearError = () => { if (expAnniversaryError) setExpAnniversaryError(''); };
        const dateInputClass = 'mt-1 w-full rounded-md border px-3 py-2 focus:border-[color:var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]/25';
        const dateInputStyle = { borderColor: expAnniversaryError ? 'var(--color-danger)' : 'var(--border-color)' };
        const dateField = (id, label, value, setValue) => (
            <div className="max-w-xs">
                <div className="flex items-center gap-2 mb-2">
                    <label className="block text-sm font-medium text-[color:var(--text-muted)]" htmlFor={id}>
                        {label}
                    </label>
                    <span
                        className="text-xs font-semibold px-1.5 py-0.5 rounded"
                        style={{ color: 'var(--color-danger)', backgroundColor: 'var(--surface-danger-subtle, var(--surface-muted))' }}
                    >
                        Required
                    </span>
                </div>
                <input
                    id={id}
                    type="date"
                    value={value}
                    onChange={(e) => { setValue(e.target.value); clearError(); }}
                    className={dateInputClass}
                    style={dateInputStyle}
                    disabled={expSaving}
                />
            </div>
        );
        return (
        <div className="space-y-6" data-tour="onboarding-exp-anniversary">
            <div>
                <h2
                    className="text-2xl font-semibold text-[color:var(--text-primary)]"
                    style={{ letterSpacing: '-0.02em' }}
                >
                    Your brokerage
                </h2>
                <p className="mt-2 text-sm text-[color:var(--text-muted)]">
                    Your brokerage and plan decide how KeyFlow works out your net income. This applies to your workspace.
                </p>
            </div>
            {existingBrokerage ? (
                <div className="max-w-xs space-y-1">
                    <p className="text-sm font-medium text-[color:var(--text-primary)]">
                        {[existingBrokerage.current.brokerageName, planLabelFor(existingBrokerage.current)].filter(Boolean).join(' · ')}
                    </p>
                    <p className="text-sm text-[color:var(--text-muted)]">To change it, go to Profile → Brokerage.</p>
                    {expAnniversaryError && (
                        <p className="mt-1 text-sm" style={{ color: 'var(--color-danger)' }}>{expAnniversaryError}</p>
                    )}
                </div>
            ) : (
            <div className="max-w-xs space-y-4">
                <BrokeragePicker
                    idPrefix="onboarding-brokerage"
                    value={brokerageChoice}
                    onChange={(next) => { setBrokerageChoice(next); clearError(); }}
                />
                {brokerageId === 'exp' && dateField('onboarding-exp-anniversary', 'Anniversary Date', expAnniversary, setExpAnniversary)}
                {needsCapYear && dateField('onboarding-cap-year-start', 'Anniversary Date', capYearStart, setCapYearStart)}
                {brokerageId === 'custom' && (
                    <>
                        <Field label="Brokerage split (%)" hint="Percent of each commission the brokerage keeps.">
                            <Input
                                id="onboarding-custom-split"
                                type="number"
                                min="0"
                                max="100"
                                step="any"
                                inputMode="decimal"
                                value={customSplit}
                                onChange={(e) => { setCustomSplit(e.target.value); clearError(); }}
                                disabled={expSaving}
                            />
                        </Field>
                        <Field label="Annual cap" hint="Leave empty for no cap.">
                            <Input
                                id="onboarding-custom-cap"
                                type="number"
                                min="0"
                                step="any"
                                inputMode="decimal"
                                value={customCap}
                                onChange={(e) => setCustomCap(e.target.value)}
                                disabled={expSaving}
                            />
                        </Field>
                    </>
                )}
                {expAnniversaryError && (
                    <p className="mt-1 text-sm" style={{ color: 'var(--color-danger)' }}>{expAnniversaryError}</p>
                )}
            </div>
            )}
            <div className="max-w-xs">
                <Field label="Time zone" hint="We use this for send times and daily digests. You can change it later in Settings, on the Profile tab.">
                    <Select
                        id="onboarding-timezone"
                        value={timezone}
                        onChange={(e) => setTimezone(e.target.value)}
                        disabled={expSaving}
                    >
                        {!timezone && <option value="">Choose your time zone</option>}
                        {timezoneOptions.map((z) => (
                            <option key={z} value={z}>{z}</option>
                        ))}
                    </Select>
                </Field>
                {/* Computed at render; the step is short-lived, so no interval is needed.
                    Not a live region: it would re-announce the time as the minute
                    changes (C-WR-04). The select announces its own value. */}
                {timezone ? (
                    <p className="text-xs text-[color:var(--text-muted)] mt-1">
                        {formatClockInZone(new Date(), timezone)}
                    </p>
                ) : (
                    <p className="text-xs text-[color:var(--text-muted)] mt-1">
                        Choose a time zone to continue.
                    </p>
                )}
            </div>
        </div>
        );
    };

    const renderGoogleStep = () => (
        <div className="space-y-6" data-tour="onboarding-google">
            <div>
                <h2
                    className="text-2xl font-semibold text-[color:var(--text-primary)]"
                    style={{ letterSpacing: '-0.02em' }}
                >
                    Connect Google
                </h2>
                <p className="mt-2 text-sm text-[color:var(--text-muted)]">
                    {isOwner
                        ? 'Connect Google to send emails, import contacts, and sync your calendar. You can skip this and connect later in Settings → Integrations.'
                        : 'Connect Google to send emails from KeyFlow. You can skip this and connect later in Settings → Integrations.'}
                </p>
            </div>
            {oauth.connected ? (
                <div
                    className="flex items-center gap-3 rounded-lg border border-[color:var(--border-color)] p-4"
                    style={{ backgroundColor: 'var(--surface-muted)' }}
                >
                    <svg
                        width="20" height="20" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        aria-hidden="true" focusable="false"
                        style={{ color: 'var(--financial-action-color)' }}
                    >
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span className="text-sm font-medium text-[color:var(--text-primary)]">Google connected</span>
                </div>
            ) : (
                <div className="space-y-3">
                    <Button
                        variant="accent"
                        onClick={() => {
                            // Defensive guard (bug: 2026-09-24-onboarding-wizard-before-plan-choice.md):
                            // oauth.initiate writes the connection against a workspace, so refuse
                            // rather than starting an OAuth round trip that has nowhere to land.
                            if (!selectedWorkspaceId) {
                                setGoogleError('No workspace is selected, so this cannot be connected right now.');
                                return;
                            }
                            setGoogleError('');
                            oauth.initiate(selectedWorkspaceId);
                        }}
                        disabled={oauth.loading}
                        loading={oauth.initiating}
                        loadingLabel={oauth.initiating ? 'Connecting…' : undefined}
                    >
                        Connect Google
                    </Button>
                    {googleError && (
                        <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{googleError}</p>
                    )}
                </div>
            )}
        </div>
    );

    const renderSmsStep = () => {
        const normalizedSmsPhone = toE164(smsPhone);
        const smsPhonePreview = normalizedSmsPhone ? formatNational(normalizedSmsPhone) : null;
        const smsPhoneInvalid = smsPhone.trim() !== '' && normalizedSmsPhone === null;

        return (
            <div className="space-y-6" data-tour="onboarding-sms">
                <div>
                    <h2
                        className="text-2xl font-semibold text-[color:var(--text-primary)]"
                        style={{ letterSpacing: '-0.02em' }}
                    >
                        SMS Notifications
                    </h2>
                    <p className="mt-2 text-sm text-[color:var(--text-muted)]">
                        Add your mobile number to receive transaction reminders by text.
                    </p>
                </div>

                <label className="block text-sm font-medium text-[color:var(--text-primary)]">
                    Mobile number
                    <input
                        type="tel"
                        value={smsPhone}
                        onChange={(e) => {
                            setSmsPhone(formatPhoneNumber(e.target.value));
                            if (smsError) setSmsError('');
                        }}
                        autoComplete="tel-national"
                        placeholder="555-123-4567"
                        className="mt-1 block w-full rounded-md border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] px-3 py-2 text-sm shadow-sm focus:border-[color:var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]/25"
                    />
                    {smsPhoneInvalid && (
                        <span className="mt-1 block text-xs" style={{ color: 'var(--color-danger)' }}>
                            Enter a valid US phone number.
                        </span>
                    )}
                    {smsPhonePreview && (
                        <span className="mt-1 block text-xs text-[color:var(--text-muted)]">
                            Will save as {smsPhonePreview}.
                        </span>
                    )}
                </label>

                <label className="flex items-start gap-2 text-sm text-[color:var(--text-primary)]">
                    <input
                        type="checkbox"
                        checked={smsConsent}
                        onChange={(e) => setSmsConsent(e.target.checked)}
                        className="mt-0.5 rounded border-[color:var(--border-color)] accent-[color:var(--color-accent)]"
                    />
                    <span>
                        {SMS_CONSENT_TEXT}
                    </span>
                </label>

                {smsError && (
                    <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{smsError}</p>
                )}
            </div>
        );
    };

    const renderWelcomeStep = () => (
        <div
            className="flex flex-col items-center text-center space-y-6"
            data-tour="onboarding-welcome"
            style={{ paddingTop: '24px', paddingBottom: '24px' }}
        >
            <div
                style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: 'var(--surface-info)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <svg
                    width="32" height="32" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    aria-hidden="true" focusable="false"
                    style={{ color: 'var(--color-accent)' }}
                >
                    <polyline points="20 6 9 17 4 12" />
                </svg>
            </div>
            <h2
                className="font-semibold text-[color:var(--text-primary)]"
                style={{ fontSize: 24, letterSpacing: '-0.02em' }}
            >
                You&rsquo;re all set!
            </h2>
            <p className="text-sm text-[color:var(--text-muted)]" style={{ maxWidth: 360 }}>
                Your account is ready. Take a quick interactive tour to get familiar with KeyFlow, or jump right in.
            </p>
            <div className="flex flex-col items-center gap-3 w-full" style={{ maxWidth: 280 }}>
                <button
                    type="button"
                    onClick={handleStartTour}
                    className="w-full inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-white bg-[color:var(--color-accent)] hover:opacity-90 transition-opacity"
                >
                    Start the tour
                </button>
                <button
                    type="button"
                    onClick={handleSkipTour}
                    className="w-full inline-flex items-center justify-center rounded-md border border-[color:var(--border-color)] px-4 py-2 text-sm font-medium text-[color:var(--text-muted)] hover:bg-[color:var(--surface-hover)]"
                >
                    Skip for now
                </button>
            </div>
        </div>
    );

    const renderStepContent = () => {
        if (stepIndex === 0) return renderTermsStep();
        if (stepIndex === 1) return renderProfileStep();
        if (stepIndex === BROKERAGE_STEP) return renderBrokerageStep();
        if (stepIndex === GOOGLE_STEP) return renderGoogleStep();
        if (stepIndex === SMS_STEP) return renderSmsStep();
        if (stepIndex === WELCOME_STEP) return renderWelcomeStep();
        return null;
    };

    // ── Shell ─────────────────────────────────────────────────────────────────
    // Portal to document.body so the wizard is independent of the React tree.
    // z-[9995]: above Modal z-[9990] and HelpMenuPanel z-[9985] — this is a
    // first-run takeover that must sit on top of all other overlays.
    return ReactDOM.createPortal(
        <div
            className={`fixed inset-0 z-[9995] flex bg-black/60 ${
                isMobile ? 'items-end justify-center' : 'items-center justify-center px-4'
            }`}
        >
            <div
                ref={containerRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="onboarding-dialog-title"
                tabIndex={-1}
                className={`flex w-full flex-col bg-[color:var(--surface-primary)] shadow-2xl focus:outline-none ${
                    isMobile ? 'h-[100dvh] rounded-none' : 'max-w-[640px] rounded-2xl'
                }`}
            >
                {/* Header — step counter + accent progress bar */}
                <div className={`border-b border-[color:var(--border-color)] ${isMobile ? 'px-4 pt-4' : 'px-6 pt-4'}`}>
                    <p className="pb-3 text-xs font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
                        Step {stepIndex + 1} of {STEP_LABELS.length} &middot; {currentLabel}
                    </p>
                    <ProgressBar stepIndex={stepIndex} total={STEP_LABELS.length} />
                </div>

                {/* Body — minHeight tracks the tallest measured step (see bodyContentRef
                    above) so the modal matches the tallest part of the flow: short steps
                    don't shrink it (Continue stays put) and it never over-extends. The
                    padded inner div is what we measure; mobile is full-dvh (no minHeight). */}
                <div
                    className="flex-1 overflow-y-auto"
                    style={isMobile || !maxBodyHeight ? undefined : { minHeight: maxBodyHeight }}
                >
                    <div ref={bodyContentRef} className={isMobile ? 'px-4 py-4' : 'px-6 py-6'}>
                        {renderStepContent()}
                    </div>
                </div>

                {/* Footer — Back / Continue / Skip (hidden on Welcome step which has its own buttons) */}
                {!isLastStep && (
                    <div
                        className={`border-t border-[color:var(--border-color)] bg-[color:var(--surface-muted)] ${
                            isMobile ? 'px-4 py-4' : 'px-6 py-4'
                        }`}
                    >
                        <div
                            className={`flex gap-3 ${
                                isMobile ? 'flex-col-reverse' : 'items-center justify-between'
                            }`}
                        >
                            {/* Left — Back or welcome label */}
                            <div>
                                {showBackButton ? (
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        className="inline-flex items-center rounded-md border border-[color:var(--border-color)] px-4 py-2 text-sm font-medium text-[color:var(--text-muted)] hover:bg-[color:var(--surface-hover)]"
                                    >
                                        Back
                                    </button>
                                ) : (
                                    <span className="text-xs font-medium uppercase tracking-wide text-[color:var(--text-muted)]">
                                        Let&rsquo;s get started
                                    </span>
                                )}
                            </div>

                            {/* Right — Skip for now (optional steps) + Continue */}
                            <div className={`flex items-center gap-3 ${isMobile ? 'flex-col' : ''}`}>
                                {/* Skip for now — shown on optional steps (Google, SMS) */}
                                {(stepIndex === GOOGLE_STEP || stepIndex === SMS_STEP) && (
                                    <button
                                        type="button"
                                        onClick={handleNext}
                                        className="text-sm font-medium text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
                                    >
                                        Skip for now
                                    </button>
                                )}
                                <Button
                                    variant="accent"
                                    onClick={handleNext}
                                    disabled={isContinueDisabled}
                                    loading={stepIndex === BROKERAGE_STEP && expSaving}
                                    loadingLabel={stepIndex === BROKERAGE_STEP && expSaving ? 'Saving…' : undefined}
                                    className={isMobile ? 'w-full justify-center' : ''}
                                >
                                    Continue
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
}

export default OnboardingWizard;
